import { useState, useEffect, useRef, Fragment, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { PlayIcon, PauseIcon, ForwardIcon } from "@heroicons/react/24/solid";
import { apiGet, apiPost } from "../../utils/api";
import { parseDate } from "../../utils/date";
import { socialLinks } from "../../shell/Header";
import { OptimizedImg } from "../../utils/OptimizedImg";
import { CATALOG } from "../store/catalog";
import { formatPrice } from "../store/useCart";
import { PageChrome } from "./PageChrome";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface MediaItem {
  id: string;
  url: string;
  type: "image" | "video" | "audio";
  thumbnailUrl?: string;
  thumbnailWebp?: string;
  mediumUrl?: string;
  filename?: string;
}

interface Update {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  pinned?: boolean;
  media?: MediaItem[];
}

interface Show {
  id: string;
  date: string;
  venue?: {
    name: string;
    address?: string;
    website?: string;
  };
  description?: string;
  thumbnail?: string;
  thumbnailWebp?: string;
  media?: { url: string; type: "image" | "video" }[];
  ticketUrl?: string;
}

interface HeroBranding {
  heroTitle: string;
  heroTagline: string;
  heroButton1Text: string;
  heroButton1Href: string;
  heroButton2Text: string;
  heroButton2Href: string;
  heroImageUrl?: string;
  heroImageOpacity?: number;
  heroButton1Bg?: string;
  heroButton1TextColor?: string;
  heroButton2Bg?: string;
  heroButton2TextColor?: string;
  palette?: string;
  showGrain?: boolean;
  marqueeItems?: string[];
  aboutText1?: string;
  aboutText2?: string;
  bookingEmail?: string;
  aboutImageUrl?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function formatDate(iso: string): string {
  return parseDate(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function monthOf(iso: string): string {
  return parseDate(iso).toLocaleDateString("en-US", { month: "short" }).toUpperCase();
}

function dayOf(iso: string): number {
  return parseDate(iso).getDate();
}

/** Same-page anchors get a plain <a>; routes get a router Link. */
function CtaLink({ href, className, children }: { href: string; className: string; children: ReactNode }) {
  return href.startsWith("#") ? (
    <a href={href} className={className}>{children}</a>
  ) : (
    <Link to={href} className={className}>{children}</Link>
  );
}

/* ── Marquee strip (OW-6; items admin-editable via branding settings, OW-16) ── */
const MARQUEE_ITEMS = ["Surf-psych from East Van", "Bring earplugs", "Join the mailing list"];

function Marquee({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  const half = [...items, ...items, ...items];
  return (
    <div className="relative z-10 my-[50px] overflow-hidden border-y border-ow-hairline bg-ow-accent py-3.5">
      <div className="ow-marquee flex w-max">
        {[0, 1].map((h) => (
          <div key={h} aria-hidden={h === 1} className="flex shrink-0 items-center">
            {half.map((item, i) => (
              <span
                key={i}
                className="flex items-center whitespace-nowrap font-anton text-2xl uppercase text-ow-on-accent"
              >
                <span className="px-6">{item}</span>
                <span className="opacity-40">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Media section (OW-8 Listen, OW-9 Watch + Photos) ── */

/** Eyebrow label + hairline rule that fills the remaining width (shared by 5a/5b/5c). */
function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="mb-6 flex items-center gap-4">
      <h3 className="shrink-0 font-grotesk text-[13px] font-bold uppercase tracking-[0.16em] text-ow-accent-3">
        {children}
      </h3>
      <span className="h-px flex-1 bg-ow-hairline" />
    </div>
  );
}

function social(name: string): string {
  return socialLinks.find((s) => s.name === name)?.href ?? "#";
}

/* Spec sample tracklist — fallback until an album is saved in admin (OW-15) */
const FALLBACK_TRACKS: AlbumTrack[] = [
  { title: "Sundowner", duration: "3:24" },
  { title: "Riptide Radio", duration: "2:58" },
  { title: "Saltwater Sunday", duration: "4:11" },
  { title: "Neon Undertow", duration: "3:46" },
  { title: "Last Good Wave", duration: "5:02" },
];

interface AlbumTrack {
  title: string;
  duration: string;
  mediaId?: string;
}

interface Album {
  title: string;
  yearLabel: string;
  coverUrl: string;
  tracks: AlbumTrack[];
}

const STREAM_BTN =
  "flex min-h-11 min-w-0 flex-1 items-center justify-center rounded-full px-2 text-center font-grotesk text-xs font-bold uppercase";

interface HomeMediaItem {
  id: string;
  title: string;
  thumbnail?: string;
  thumbnailWebp?: string;
  addedAt?: string;
}

function MediaSection() {
  const [videos, setVideos] = useState<HomeMediaItem[]>([]);
  const [photos, setPhotos] = useState<HomeMediaItem[]>([]);
  const [album, setAlbum] = useState<Album | null>(null);

  /* GET /media is public; the API already filters private items for guests
     and returns newest first. */
  useEffect(() => {
    apiGet<{ items: HomeMediaItem[] }>("/media?type=video&limit=3")
      .then((r) => setVideos(r.items))
      .catch(() => {});
    apiGet<{ items: HomeMediaItem[] }>("/media?type=image&limit=6")
      .then((r) => setPhotos(r.items))
      .catch(() => {});
    apiGet<Album>("/album")
      .then(setAlbum)
      .catch(() => {});
  }, []);

  const albumTitle = album?.title || "Crème De La Mer";
  const yearLabel = album?.yearLabel || "2026 · Self-released";
  const coverUrl = album?.coverUrl || "";
  const tracks = album?.tracks?.length ? album.tracks : FALLBACK_TRACKS;
  const anyPlayable = tracks.some((t) => t.mediaId);

  /* Inline playback: one shared <audio>. A track's presigned URL is fetched on
     hover/touch (prefetch) or first tap, then cached so later taps call play()
     synchronously inside the gesture (Safari's autoplay rule). */
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement>(null);
  const urlCache = useRef(new Map<string, string>());
  const inflight = useRef(new Map<string, Promise<string>>());
  const wanted = useRef<number | null>(null); // last tapped track
  const [current, setCurrent] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loadingIdx, setLoadingIdx] = useState<number | null>(null);
  const [time, setTime] = useState({ at: 0, total: 0 });

  /* The now-playing pill only shows while the tracklist is off-screen */
  const listRef = useRef<HTMLUListElement>(null);
  const [listInView, setListInView] = useState(true);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setListInView(e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  function fetchUrl(id: string): Promise<string> {
    let p = inflight.current.get(id);
    if (!p) {
      p = apiGet<{ url: string }>(`/media?id=${id}`).then((r) => {
        urlCache.current.set(id, r.url);
        return r.url;
      });
      p.catch(() => inflight.current.delete(id));
      inflight.current.set(id, p);
    }
    return p;
  }

  function start(i: number, url: string) {
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = url;
    setCurrent(i);
    setTime({ at: 0, total: 0 });
    audio.play().catch((err: DOMException) => {
      // NotAllowed: the gesture expired while fetching — URL is cached now, next tap plays
      if (err.name !== "NotAllowedError" && err.name !== "AbortError") navigate(`/media/${tracks[i].mediaId}`);
    });
  }

  async function toggle(i: number) {
    const audio = audioRef.current;
    const id = tracks[i].mediaId;
    if (!audio || !id) return;
    wanted.current = i;
    // An errored source (expired presigned URL) falls through to a refetch
    if (i === current && !audio.error) {
      if (audio.paused) audio.play().catch(() => {});
      else audio.pause();
      return;
    }
    const cached = urlCache.current.get(id);
    if (cached) return start(i, cached);
    setLoadingIdx(i);
    try {
      const url = await fetchUrl(id);
      if (wanted.current === i) start(i, url); // a later tap wins over a slow fetch
    } catch {
      if (wanted.current === i) navigate(`/media/${id}`); // can't play inline — fall back to the media page
    } finally {
      setLoadingIdx((l) => (l === i ? null : l));
    }
  }

  function playNext() {
    const next = current === null ? -1 : tracks.findIndex((t, j) => j > current && t.mediaId);
    if (next >= 0) void toggle(next);
  }

  const TRACK_ROW = "flex items-center gap-4 border-b border-ow-hairline px-2 py-3.5";

  return (
    <section id="media" className="mx-auto w-full max-w-[1100px] px-7 py-14 font-grotesk">
      <h2 className="mb-10 font-anton text-[clamp(40px,7vw,84px)] uppercase leading-[0.92] text-ow-text">
        Media
      </h2>

      {/* ── 5a. Listen (album data admin-editable, OW-15) ── */}
      <Eyebrow>Listen</Eyebrow>
      <div className="grid gap-8 md:grid-cols-[minmax(0,300px)_1fr]">
        <div>
          <div className="ow-hatch aspect-square overflow-hidden rounded-2xl border border-ow-hairline">
            {coverUrl && (
              <img src={coverUrl} alt={`${albumTitle} album art`} loading="lazy" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            )}
          </div>
          <div className="mt-4 font-cooper text-[22px] font-semibold italic text-ow-accent">
            {albumTitle}
          </div>
          <div className="text-sm text-ow-dim">{yearLabel}</div>
          <div className="mt-4 flex gap-2">
            <a href={social("Spotify")} target="_blank" rel="noreferrer" className={`${STREAM_BTN} bg-ow-accent text-ow-on-accent`}>
              Spotify
            </a>
            <a href={social("YouTube")} target="_blank" rel="noreferrer" className={`${STREAM_BTN} border border-ow-hairline bg-[oklch(0.95_0.015_80/0.08)] text-ow-text`}>
              YouTube
            </a>
            <a href={social("SoundCloud")} target="_blank" rel="noreferrer" className={`${STREAM_BTN} border border-ow-hairline bg-[oklch(0.95_0.015_80/0.08)] text-ow-text`}>
              SoundCloud
            </a>
          </div>
        </div>
        <div>
        <ul ref={listRef}>
          {tracks.map((t, i) => {
            const isCurrent = i === current;
            const pct = isCurrent && time.total ? (time.at / time.total) * 100 : 0;
            const id = t.mediaId;
            const number = (
              <span className="w-5 shrink-0 font-anton text-[15px] text-ow-accent-3">
                {String(i + 1).padStart(2, "0")}
              </span>
            );
            /* Only playable rows show play/pause; inert rows keep the slot in a mixed list so titles align */
            const icon = (
              <span
                aria-hidden="true"
                className={`w-4 shrink-0 text-ow-accent transition-[filter,color,transform] duration-200 group-hover/play:scale-110 group-hover/play:text-ow-text group-hover/play:[filter:drop-shadow(0_0_3px_var(--ow-accent))_drop-shadow(0_0_10px_var(--ow-accent))] group-focus-visible/play:text-ow-text group-focus-visible/play:[filter:drop-shadow(0_0_3px_var(--ow-accent))_drop-shadow(0_0_10px_var(--ow-accent))] ${id ? "" : anyPlayable ? "invisible" : "hidden"} ${
                  loadingIdx === i ? "motion-safe:animate-pulse" : ""
                }`}
              >
                {isCurrent && playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
              </span>
            );
            const titleClass = `min-w-0 flex-1 truncate text-base font-semibold ${isCurrent ? "text-ow-accent" : "text-ow-text"}`;
            // Full-row-height hit areas: number + arrow play inline, the title opens the media item
            const STRETCH = "-my-3.5 flex items-center self-stretch py-3.5";
            return (
              <li key={i}>
                <div className={`${TRACK_ROW} relative ${id ? "transition-colors hover:bg-ow-surface" : ""} ${isCurrent ? "bg-ow-surface" : ""}`}>
                  {id ? (
                    <button
                      type="button"
                      onClick={() => void toggle(i)}
                      onPointerEnter={() => void fetchUrl(id).catch(() => {})}
                      onFocus={() => void fetchUrl(id).catch(() => {})}
                      aria-label={`${isCurrent && playing ? "Pause" : "Play"} ${t.title}`}
                      className={`group/play ${STRETCH} -ml-2 shrink-0 gap-4 pl-2 pr-1`}
                    >
                      {number}
                      {icon}
                    </button>
                  ) : (
                    <>
                      {number}
                      {icon}
                    </>
                  )}
                  {id ? (
                    <Link to={`/media/${id}`} className={`group ${STRETCH} ${titleClass} transition-colors hover:text-ow-accent focus-visible:text-ow-accent`}>
                      <span className="truncate decoration-ow-accent decoration-2 underline-offset-4 group-hover:underline group-focus-visible:underline">
                        {t.title}
                      </span>
                    </Link>
                  ) : (
                    <span className={titleClass}>{t.title}</span>
                  )}
                  <span className="text-sm tabular-nums text-ow-dim">
                    {isCurrent && time.at > 0 ? `${clock(time.at)}${time.total ? ` / ${clock(time.total)}` : ""}` : t.duration}
                  </span>
                  {/* Progress fills the row's bottom hairline */}
                  {isCurrent && (
                    <span aria-hidden="true" className="absolute -bottom-px left-0 h-0.5 bg-ow-accent" style={{ width: `${pct}%` }} />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {!album?.tracks?.length && <p className="mt-3 text-[13px] text-ow-dimmer">Sample tracklist.</p>}
        <p className="sr-only" aria-live="polite">
          {current !== null ? `${playing ? "Playing" : "Paused"}: ${tracks[current].title}` : ""}
        </p>
        {current !== null && !listInView && (
          <NowPlaying
            title={tracks[current].title}
            playing={playing}
            hasNext={tracks.some((t, j) => j > current && t.mediaId)}
            onToggle={() => void toggle(current)}
            onNext={playNext}
            onJump={() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
          />
        )}
        <audio
          ref={audioRef}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setTime({ at: e.currentTarget.currentTime, total: e.currentTarget.duration || 0 })}
          onEnded={playNext}
          onError={() => {
            // presigned URL expired or unplayable — forget it so the next tap refetches
            const id = current === null ? undefined : tracks[current].mediaId;
            if (id) {
              urlCache.current.delete(id);
              inflight.current.delete(id);
            }
          }}
        />
        </div>
      </div>

      {/* ── 5b. Watch ── */}
      {videos.length > 0 && (
        <div className="mt-14">
          <Eyebrow>Watch</Eyebrow>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5">
            {videos.map((v, i) => (
              <Link key={v.id} to={`/media/${v.id}`} className={`group block ${i > 0 ? "hidden sm:block" : ""}`}>
                <div className="ow-hatch relative aspect-video overflow-hidden rounded-[14px] border border-ow-hairline transition-colors group-hover:border-ow-accent">
                  {v.thumbnail && (
                    <OptimizedImg
                      webpSrc={v.thumbnailWebp}
                      src={v.thumbnail}
                      alt={v.title}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="grid h-[54px] w-[54px] place-items-center rounded-full border-[1.5px] border-[oklch(0.95_0.015_80/0.7)] bg-[oklch(0.13_0.02_45/0.5)] pl-0.5 text-ow-text backdrop-blur transition-[color,border-color,filter] duration-200 group-hover:border-ow-accent group-hover:text-ow-accent group-hover:drop-shadow-[0_0_8px_var(--ow-accent)]">
                      <PlayIcon aria-hidden="true" className="h-5 w-5" />
                    </span>
                  </div>
                  {/* ponytail: no duration field on media items yet — chip lands when the API grows one */}
                </div>
                <div className="mt-2.5 truncate text-base font-bold text-ow-text">{v.title}</div>
                {v.addedAt && <div className="text-[13px] text-ow-dim">{formatDate(v.addedAt)}</div>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── 5c. Photos ── */}
      {photos.length > 0 && (
        <div className="mt-14">
          <Eyebrow>Photos</Eyebrow>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-3.5 lg:grid-cols-6">
            {photos.map((p, i) => (
              <Link
                key={p.id}
                to={`/media/${p.id}`}
                className={`ow-hatch aspect-square overflow-hidden rounded-xl border border-ow-hairline transition-all hover:scale-[1.02] hover:border-ow-accent ${
                  i >= 4 ? "hidden sm:block" : ""
                }`}
              >
                {p.thumbnail && (
                  <OptimizedImg
                    webpSrc={p.thumbnailWebp}
                    src={p.thumbnail}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <Link to="/media" className={`${ABOUT_PILL} inline-block`}>
          See all media →
        </Link>
      </div>
    </section>
  );
}

/* ── Merch section (OW-10) ── */

function MerchSection() {
  // ponytail: the store's product listing IS the static CATALOG (server mirrors it for checkout)
  const products = CATALOG.slice(0, 4);
  if (products.length === 0) return null;

  return (
    <section id="merch" className="mx-auto w-full max-w-[1100px] px-7 py-14 font-grotesk">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-anton text-[clamp(40px,7vw,84px)] uppercase leading-[0.92] text-ow-text">
          Merch
        </h2>
        <Link to="/store" className={ABOUT_PILL}>
          Visit full store →
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] sm:gap-[22px]">
        {products.map((p) => (
          <Link key={p.id} to={`/store/${p.slug}`} className="group block">
            <div className="ow-hatch relative aspect-square overflow-hidden rounded-2xl border border-ow-hairline transition-all duration-200 group-hover:-translate-y-1 group-hover:border-ow-accent">
              {p.hero_image && (
                // ponytail: catalog images are placeholder paths today — hide on 404, hatch shows through
                <img
                  src={p.hero_image}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
              {p.tag && (
                <span className="absolute left-3 top-3 rounded-full bg-ow-accent-3 px-2.5 py-1 text-[11px] font-bold uppercase text-ow-on-accent">
                  {p.tag}
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
              <span className="min-w-0 text-base font-bold text-ow-text">{p.title}</span>
              <span className="font-anton text-lg text-ow-accent">
                {formatPrice(p.price_cents, p.currency)}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ── About section (OW-11) ── */

const ABOUT_PILL =
  "rounded-full border-[1.5px] border-ow-ghost-border px-6 py-3 font-grotesk text-[13px] font-bold uppercase tracking-[0.06em] text-ow-text transition-colors hover:border-ow-accent";

function AboutSection({ hero }: { hero: HeroBranding }) {
  return (
    <section id="about" className="mx-auto w-full max-w-[1100px] px-7 py-14 font-grotesk">
      <div className="grid items-center gap-10 md:grid-cols-[1fr_minmax(0,420px)]">
        <div>
          <h2 className="font-anton text-[clamp(40px,7vw,84px)] uppercase leading-[0.92] text-ow-text">
            The
            <br />
            <span className="text-ow-accent">Band</span>
          </h2>
          {/* Copy + booking email are admin-editable via branding settings (OW-20) */}
          <p className="mt-6 max-w-[48ch] text-lg leading-relaxed text-ow-text">
            {hero.aboutText1 || DEFAULT_HERO.aboutText1}
          </p>
          <p className="mt-4 max-w-[48ch] text-base leading-relaxed text-ow-dim">
            {hero.aboutText2 || DEFAULT_HERO.aboutText2}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/press" className={ABOUT_PILL}>
              Press Kit
            </Link>
            <a href={`mailto:${hero.bookingEmail || DEFAULT_HERO.bookingEmail}`} className={ABOUT_PILL}>
              Booking
            </a>
          </div>
        </div>
        {/* Portrait admin-uploadable (OW-22); hatch placeholder when unset */}
        <div className="ow-hatch aspect-[4/5] overflow-hidden rounded-[18px] border border-ow-hairline">
          {hero.aboutImageUrl && (
            <img src={hero.aboutImageUrl} alt="Band portrait" loading="lazy" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Mailing list (OW-12) ── */

function MailingListSection() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFailed(false);
    try {
      await apiPost("/subscribe", { email });
      setSent(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="mailing" className="mx-auto w-full max-w-[1100px] px-7 py-14 font-grotesk">
      <div
        className="rounded-[24px] p-[clamp(32px,5vw,60px)] text-center text-ow-on-accent"
        style={{ background: "linear-gradient(135deg, var(--ow-accent), var(--ow-accent-2))" }}
      >
        {sent ? (
          <h2 className="font-anton text-[clamp(32px,5.5vw,60px)] uppercase leading-[0.92]">
            You're on the list ✦ See you out there
          </h2>
        ) : (
          <>
            <h2 className="font-anton text-[clamp(32px,5.5vw,60px)] uppercase leading-[0.92]">
              Don't miss a show
            </h2>
            <p className="mx-auto mt-3 max-w-[46ch] font-semibold opacity-80">
              Tour dates, new music, and first dibs on tickets — straight to your inbox.
            </p>
            <form onSubmit={submit} className="mx-auto mt-7 flex w-full max-w-[520px] flex-wrap justify-center gap-3">
              <label htmlFor="ow-subscribe-email" className="sr-only">
                Email address
              </label>
              <input
                id="ow-subscribe-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@earth.com"
                className="min-w-0 flex-1 basis-[260px] rounded-xl px-[18px] py-4 text-ow-text placeholder:text-[oklch(0.66_0.02_80)] focus:outline-none focus:ring-2 focus:ring-ow-on-accent"
                style={{ background: "oklch(0.16 0.02 45 / 0.92)" }}
              />
              <button
                type="submit"
                disabled={busy}
                className="rounded-full bg-ow-on-accent px-7 py-4 font-bold uppercase text-ow-text disabled:opacity-60"
              >
                {busy ? "…" : "Sign Up"}
              </button>
            </form>
            {failed && (
              <p className="mt-3 text-sm font-bold" role="alert">
                Couldn’t sign you up just now — check the address and try again.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const DEFAULT_HERO: HeroBranding = {
  heroTitle: "Orange Whip",
  heroTagline: "Industrial Surf",
  heroButton1Text: "Listen Now",
  heroButton1Href: "#media",
  heroButton2Text: "Shop Merch",
  heroButton2Href: "#merch",
  // ponytail: bundled default hero photo; empty admin value hides the photo layer (OW-13 wires upload)
  heroImageUrl: "/hero-surfer.jpg",
  palette: "sunset",
  showGrain: true,
  marqueeItems: MARQUEE_ITEMS,
  aboutText1:
    "Orange Whip started in a damp East Van practice space chasing one thing: the sound of the last good wave before the fog rolls in. Equal parts surf twang, fuzz-pedal psych, and late-night garage swagger.",
  aboutText2: "Four records, a hundred sweaty rooms, and zero plans to slow down. Bring earplugs.",
  bookingEmail: "hello@orangewhip.surf",
};

export function HomePage() {
  const [hero, setHero] = useState<HeroBranding>(DEFAULT_HERO);
  const [pinnedUpdate, setPinnedUpdate] = useState<Update | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [mediaIdx, setMediaIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        /* Try batch endpoint first (single request) */
        const data = await apiGet<{
          branding: HeroBranding;
          pinnedUpdate: Update | null;
          upcomingShows: Show[];
        }>("/homepage");

        if (cancelled) return;

        setHero({ ...DEFAULT_HERO, ...data.branding });
        setPinnedUpdate(data.pinnedUpdate);
        setShows(data.upcomingShows);
      } catch {
        /* Fallback: parallel calls if /homepage not available */
        if (cancelled) return;

        const [brandingResult, updateResult, showsResult] =
          await Promise.allSettled([
            apiGet<HeroBranding>("/branding").catch(() => DEFAULT_HERO),
            apiGet<Update>("/updates/pinned").catch(async () => {
              try {
                const all = await apiGet<Update[]>("/updates");
                return all.length ? all[0] : null;
              } catch {
                return null;
              }
            }),
            apiGet<Show[]>("/shows").catch(() => [] as Show[]),
          ]);

        if (cancelled) return;

        const branding =
          brandingResult.status === "fulfilled"
            ? brandingResult.value
            : DEFAULT_HERO;
        setHero({ ...DEFAULT_HERO, ...branding });

        const update =
          updateResult.status === "fulfilled" ? updateResult.value : null;
        setPinnedUpdate(update);

        const showsData =
          showsResult.status === "fulfilled" ? showsResult.value : [];
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const upcoming = showsData
          .filter((s) => parseDate(s.date) >= now)
          .sort(
            (a, b) =>
              parseDate(a.date).getTime() - parseDate(b.date).getTime(),
          )
          .slice(0, 6);
        setShows(upcoming);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Render ── */

  return (
    <main className="relative bg-ow-bg">
      {/* ── Hero (OW-5) — pinned; the rest of the page scrolls over it (OW-19) ── */}
      <section className="sticky top-0 z-0 -mt-[88px] h-[100svh] overflow-hidden">
        {/* Full-bleed photo layer — hidden entirely when no image is set */}
        {hero.heroImageUrl && (
          <div className="absolute inset-0 z-[1] overflow-hidden">
            {/* ponytail: lowercase spread — React 18 doesn't know fetchPriority; switch to the prop on React 19 */}
            <img src={hero.heroImageUrl} alt="" {...{ fetchpriority: "high" }} className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            {/* Legibility gradient — keep regardless of chosen image */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, oklch(0.16 0.018 45 / 0.45) 0%, oklch(0.16 0.018 45 / 0.62) 55%, oklch(0.16 0.018 45) 100%)",
              }}
            />
          </div>
        )}

        <div className="relative z-10 mx-auto flex h-full max-w-[1240px] flex-col items-center justify-center px-7 pt-[88px] text-center">
          {/* Spinning sun behind the wordmark (outer div centers, inner rotates) */}
          <div className="absolute left-1/2 top-[38%] -z-[1] h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 opacity-[0.16] blur-2xl">
            <div
              className="ow-sun h-full w-full rounded-full"
              style={{
                background:
                  "conic-gradient(var(--ow-accent), var(--ow-accent-3), var(--ow-accent-2), var(--ow-accent))",
              }}
            />
          </div>

          {/* Eyebrow pill */}
          <div className="inline-flex items-center gap-2.5 rounded-full border border-ow-hairline bg-[oklch(0.95_0.015_80/0.07)] px-4 py-2 backdrop-blur">
            <span className="h-2 w-2 rounded-full bg-ow-accent-3 shadow-[0_0_10px_2px_var(--ow-accent-3)]" />
            <span className="font-grotesk text-[13px] font-semibold uppercase tracking-[0.06em] text-ow-text">
              Vancouver, BC · Surf-Psych Rock
            </span>
          </div>

          {/* Wordmark — one word per line */}
          <h1
            className="mt-6 font-cooper text-[clamp(64px,14vw,210px)] font-semibold italic leading-[0.92] text-ow-accent"
            style={{
              textShadow:
                "0.035em 0.045em 0 oklch(0.14 0.02 45), 0.06em 0.08em 0 oklch(0.14 0.02 45 / 0.5)",
            }}
          >
            {hero.heroTitle.split(" ").map((word, i, arr) => (
              <Fragment key={i}>
                {word}
                {i < arr.length - 1 && <br />}
              </Fragment>
            ))}
          </h1>

          {/* Lead */}
          <p className="mt-6 max-w-[560px] font-grotesk text-[clamp(16px,2.2vw,20px)] leading-relaxed text-[oklch(0.86_0.02_80)]">
            Reverb-drenched riffs and sunburnt melodies from the Pacific Northwest. Catch the
            wave live.
          </p>

          {/* CTAs — labels/links come from branding settings; admin reconciliation in OW-13 */}
          <div className="mt-8 flex flex-wrap justify-center gap-3.5">
            <CtaLink
              href={hero.heroButton1Href}
              className="rounded-full bg-ow-accent px-8 py-4 font-grotesk text-base font-bold uppercase tracking-[0.03em] text-ow-on-accent shadow-[0_14px_40px_-14px_var(--ow-accent)] transition-transform duration-200 hover:-translate-y-0.5 hover:scale-[1.02]"
            >
              {hero.heroButton1Text}
            </CtaLink>
            <CtaLink
              href={hero.heroButton2Href}
              className="rounded-full border-[1.5px] border-ow-ghost-border px-8 py-4 font-grotesk text-base font-bold uppercase tracking-[0.03em] text-ow-text transition-all duration-200 hover:-translate-y-0.5 hover:border-ow-accent-3"
            >
              {hero.heroButton2Text}
            </CtaLink>
          </div>

          {/* Next-show ribbon — hidden when no upcoming shows */}
          {!loading && shows[0] && (
            <div className="mt-10 flex w-full max-w-[760px] flex-wrap items-center gap-5 rounded-[18px] border border-ow-hairline bg-[oklch(0.95_0.015_80/0.05)] px-[22px] py-[18px] text-left backdrop-blur sm:flex-nowrap">
              <div className="text-center font-anton leading-[0.85]">
                <div className="text-[15px] uppercase tracking-[0.1em] text-ow-accent-3">
                  {monthOf(shows[0].date)}
                </div>
                <div className="text-[32px] text-ow-text">{dayOf(shows[0].date)}</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-grotesk text-xs font-bold uppercase tracking-[0.16em] text-ow-dim">
                  Next Show
                </div>
                <div className="truncate font-grotesk text-lg font-bold text-ow-text">
                  {shows[0].venue?.name ?? "TBA"}
                </div>
                {shows[0].venue?.address && (
                  <div className="truncate font-grotesk text-sm text-ow-dim">
                    {shows[0].venue.address}
                  </div>
                )}
              </div>
              <a
                href="#shows"
                className="whitespace-nowrap rounded-full border border-ow-hairline-strong px-[18px] py-[11px] font-grotesk text-[12.5px] font-bold uppercase tracking-[0.08em] text-ow-text transition-colors hover:border-transparent hover:bg-ow-accent hover:text-ow-on-accent"
              >
                All Dates
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Everything below scrolls over the pinned hero (OW-19) — opaque bg required.
          overflow-hidden stops the marquee's top margin collapsing through and
          opening a see-through gap. */}
      <div className="relative z-10 overflow-hidden bg-ow-bg">
      <PageChrome showGrain={hero.showGrain !== false} />

      {/* ── Marquee (OW-6, admin-editable OW-16) ── */}
      <Marquee items={hero.marqueeItems ?? MARQUEE_ITEMS} />

      <div className="relative z-10">
      {/* ── Show Dates (OW-7) ── */}
      <section id="shows" className="relative z-10 mx-auto w-full max-w-[1100px] px-7 pb-14 pt-4">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-anton text-[clamp(40px,7vw,84px)] uppercase leading-[0.92] text-ow-text">
            Show
            <br />
            <span className="text-ow-accent">Dates</span>
          </h2>
          <p className="max-w-[340px] font-grotesk text-[15px] text-ow-dim">
            {!loading && shows.length === 0
              ? "Doors usually 8pm. New dates go to the mailing list first."
              : "Doors usually 8pm. Grab tickets early — most rooms are small and these go fast."}
          </p>
        </div>

        <div className="mt-10 flex flex-col gap-0.5 overflow-hidden rounded-[18px] border border-ow-hairline">
          {!loading && shows.length === 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 bg-ow-surface px-6 py-5 font-grotesk text-ow-dim">
              <Link
                to="/shows"
                className="text-ow-text transition-colors hover:text-ow-accent focus-visible:text-ow-accent"
              >
                No upcoming shows at the moment. Check out media from our past shows.
              </Link>
              <a
                href="#mailing"
                className="whitespace-nowrap rounded-full bg-ow-accent px-[18px] py-[11px] text-[12.5px] font-bold uppercase tracking-[0.08em] text-ow-on-accent transition-opacity hover:opacity-90"
              >
                Join the list
              </a>
            </div>
          )}
          {shows.map((show) => (
            <Link
              key={show.id}
              to={`/shows/${show.id}`}
              className="grid grid-cols-[72px_1fr_auto] items-center gap-3 bg-ow-surface px-4 py-5 transition-colors hover:bg-ow-surface-hover sm:grid-cols-[96px_1fr_auto] sm:gap-5 sm:px-6"
            >
              <div className="font-anton leading-[0.85]">
                <div className="text-[15px] uppercase tracking-[0.1em] text-ow-accent-3">
                  {monthOf(show.date)}
                </div>
                <div className="text-[32px] text-ow-text">{dayOf(show.date)}</div>
              </div>
              <div className="min-w-0">
                <div className="truncate font-grotesk text-lg font-bold text-ow-text">
                  {show.venue?.name ?? "TBA"}
                </div>
                <div className="truncate font-grotesk text-sm text-ow-dim">
                  {show.venue?.address}
                  {show.description ? ` · ${show.description}` : ""}
                </div>
              </div>
              {/* ponytail: unified orange Details pill by design — no ticket-state variants */}
              <span className="whitespace-nowrap rounded-full bg-ow-accent px-[18px] py-[11px] font-grotesk text-[12.5px] font-bold uppercase tracking-[0.08em] text-ow-on-accent">
                Details
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Latest News (moved between Show Dates and Media, OW-17) ── */}
      {!loading && pinnedUpdate && isFresh(pinnedUpdate.createdAt) && (
        <section className="mx-auto w-full max-w-[1100px] px-7 py-14 font-grotesk">
          <h2 className="mb-10 font-anton text-[clamp(40px,7vw,84px)] uppercase leading-[0.92] text-ow-text">
            Latest
            <br />
            <span className="text-ow-accent">News</span>
          </h2>

          <div className="rounded-[18px] border border-ow-hairline bg-ow-surface p-6 sm:p-8">
            <div className="flex flex-col gap-6 sm:flex-row">
              {pinnedUpdate.media?.[0] && (
                <div className="ow-hatch aspect-video flex-shrink-0 overflow-hidden rounded-[14px] border border-ow-hairline sm:aspect-auto sm:h-36 sm:w-48">
                  {pinnedUpdate.media[0].type === "image" ? (
                    <OptimizedImg
                      webpSrc={pinnedUpdate.media[0].thumbnailWebp}
                      src={pinnedUpdate.media[0].thumbnailUrl || pinnedUpdate.media[0].url}
                      alt={pinnedUpdate.title}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-ow-text">
                      <PlayTriangle />
                    </div>
                  )}
                </div>
              )}

              <div className="min-w-0 flex-1">
                {pinnedUpdate.pinned && (
                  <span className="mb-2 inline-block text-[13px] font-bold uppercase tracking-[0.16em] text-ow-accent-3">
                    Pinned
                  </span>
                )}
                <h3 className="font-grotesk text-lg font-bold text-ow-text">{pinnedUpdate.title}</h3>
                <p className="mt-2 line-clamp-3 text-ow-dim">
                  <Linkified text={pinnedUpdate.content} />
                </p>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[13px] text-ow-dimmer">{formatDate(pinnedUpdate.createdAt)}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(true);
                      setMediaIdx(0);
                    }}
                    className="whitespace-nowrap rounded-full border border-ow-hairline-strong px-[18px] py-[11px] text-[12.5px] font-bold uppercase tracking-[0.08em] text-ow-text transition-colors hover:border-transparent hover:bg-ow-accent hover:text-ow-on-accent"
                  >
                    Read more
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Media (OW-8, OW-9) ── */}
      <MediaSection />

      {/* ── Merch (OW-10) ── */}
      <MerchSection />

      {/* ── About (OW-11; copy editable via OW-20) ── */}
      <AboutSection hero={hero} />

      {/* ── Mailing list (OW-12) ── */}
      <MailingListSection />

      {/* ── Update Detail Modal ── */}
      <Transition appear show={modalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-[oklch(0.13_0.02_45/0.85)]" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl rounded-[18px] border border-ow-hairline-strong bg-ow-bg p-6 font-grotesk sm:p-8">
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <Dialog.Title className="font-anton text-[clamp(28px,4.5vw,44px)] uppercase leading-[0.92] text-ow-text">
                      {pinnedUpdate?.title}
                    </Dialog.Title>
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={() => setModalOpen(false)}
                      className="-mr-2.5 -mt-2.5 rounded-full p-2.5 text-ow-dim transition-colors hover:text-ow-accent"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Media carousel */}
                  {pinnedUpdate?.media && pinnedUpdate.media.length > 0 && (
                    <div className="mb-6">
                      <div className="ow-hatch relative aspect-video overflow-hidden rounded-[14px] border border-ow-hairline">
                        {pinnedUpdate.media[mediaIdx].type === "image" ? (
                          <OptimizedImg
                            webpSrc={pinnedUpdate.media[mediaIdx].mediumUrl}
                            src={pinnedUpdate.media[mediaIdx].url}
                            className="w-full h-full object-contain"
                          />
                        ) : pinnedUpdate.media[mediaIdx].type === "video" ? (
                          <video
                            src={pinnedUpdate.media[mediaIdx].url}
                            controls
                            className="w-full h-full"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <audio
                              src={pinnedUpdate.media[mediaIdx].url}
                              controls
                            />
                          </div>
                        )}
                      </div>

                      {pinnedUpdate.media.length > 1 && (
                        <div className="mt-2 flex justify-center">
                          {pinnedUpdate.media.map((_, i) => (
                            <button
                              key={i}
                              type="button"
                              aria-label={`Show item ${i + 1}`}
                              aria-current={i === mediaIdx}
                              onClick={() => setMediaIdx(i)}
                              className="group flex h-11 w-11 items-center justify-center"
                            >
                              <span
                                className={`h-2.5 w-2.5 rounded-full transition-colors ${
                                  i === mediaIdx ? "bg-ow-accent" : "bg-ow-hairline-strong group-hover:bg-ow-dim"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <p className="whitespace-pre-wrap leading-relaxed text-ow-dim">
                    <Linkified text={pinnedUpdate?.content ?? ""} />
                  </p>

                  <p className="mt-4 text-[13px] text-ow-dimmer">
                    {pinnedUpdate && formatDate(pinnedUpdate.createdAt)}
                  </p>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      </div>
      </div>
    </main>
  );
}

/* ── Tiny inline play icon ── */
function PlayTriangle() {
  return (
    <svg className="w-10 h-10" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

/** m:ss for the track clock */
function clock(sec: number): string {
  return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;
}

/* A news post older than this reads as stale "Latest News" — hide it instead */
const NEWS_MAX_AGE_DAYS = 60;
function isFresh(createdAt: string): boolean {
  return Date.now() - parseDate(createdAt).getTime() < NEWS_MAX_AGE_DAYS * 86_400_000;
}

/** Plain text with bare http(s) URLs turned into links */
function Linkified({ text }: { text: string }) {
  return (
    <>
      {text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
        i % 2 ? (
          <a key={i} href={part} target="_blank" rel="noreferrer" className="break-all text-ow-accent underline underline-offset-2">
            {part}
          </a>
        ) : (
          part
        ),
      )}
    </>
  );
}

/** Floating now-playing control, shown while a track plays and the tracklist is scrolled away. */
function NowPlaying({
  title,
  playing,
  hasNext,
  onToggle,
  onNext,
  onJump,
}: {
  title: string;
  playing: boolean;
  hasNext: boolean;
  onToggle: () => void;
  onNext: () => void;
  onJump: () => void;
}) {
  return (
    <div className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 flex items-center gap-2 rounded-full border border-ow-hairline-strong bg-ow-glass p-1.5 font-grotesk backdrop-blur-[14px] sm:left-auto sm:right-6 sm:w-[360px]">
      <button
        type="button"
        onClick={onToggle}
        aria-label={playing ? `Pause ${title}` : `Play ${title}`}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ow-accent text-ow-on-accent transition-opacity hover:opacity-90"
      >
        {playing ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5 pl-0.5" />}
      </button>
      <button type="button" onClick={onJump} className="min-w-0 flex-1 text-left" aria-label={`Show ${title} in the tracklist`}>
        <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-ow-accent-3">Now playing</span>
        <span className="block truncate text-sm font-semibold text-ow-text">{title}</span>
      </button>
      {hasNext && (
        <button
          type="button"
          onClick={onNext}
          aria-label="Next track"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-ow-text transition-colors hover:text-ow-accent"
        >
          <ForwardIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
